// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import { Button, Group, Modal, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMedplum } from '@medplum/react';
import { type JSX, useState } from 'react';
import { botIdentifier } from '../../lib/bot-identifier';
import { showErrorNotification } from '../../utils/notifications';

interface EnableProviderModalProps {
  opened: boolean;
  onClose: () => void;
  practitioner: {
    id: string;
    name: string;
    email: string;
  };
  onSuccess: () => void;
}

/**
 * Confirmation modal for re-enabling a disabled provider.
 *
 * Sets ProjectMembership.active = true via the enable-provider bot.
 */
export function EnableProviderModal({
  opened,
  onClose,
  practitioner,
  onSuccess,
}: EnableProviderModalProps): JSX.Element {
  const medplum = useMedplum();
  const [loading, setLoading] = useState(false);

  const handleClose = (): void => {
    onClose();
  };

  const handleConfirm = async (): Promise<void> => {
    setLoading(true);
    try {
      await medplum.executeBot(botIdentifier('enable-provider'), {
        practitionerReference: `Practitioner/${practitioner.id}`,
      });
      notifications.show({
        color: 'green',
        title: 'Provider enabled',
        message: `${practitioner.name} has been enabled and can now sign in to the provider portal.`,
      });
      onSuccess();
    } catch (err) {
      showErrorNotification(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal opened={opened} onClose={handleClose} title="Enable provider" size="md" data-testid="enable-provider-modal">
      <Stack gap="md">
        <Text size="sm">
          Enable{' '}
          <Text component="span" fw={600}>
            {practitioner.name}
          </Text>{' '}
          (
          <Text component="span" fs="italic">
            {practitioner.email}
          </Text>
          )? They will regain access to the provider portal.
        </Text>
        <Group justify="flex-end" gap="sm">
          <Button
            variant="default"
            onClick={handleClose}
            disabled={loading}
            data-testid="enable-provider-cancel-button"
          >
            Cancel
          </Button>
          <Button
            color="green"
            loading={loading}
            onClick={() => {
              handleConfirm().catch(console.error);
            }}
            data-testid="enable-provider-confirm-button"
          >
            Enable provider
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
